// useSchemaSuggestions — TanStack Query hooks backing VQE-Edit's
// suggestion-driven popovers (column chooser, table chooser).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove SELECT
// column. Future filter/join slices add their own hooks alongside.
//
// Caching strategy: keyed by the resolver service identity (connectionId +
// engineId), the documentId/statementId, and the filter prefix. The query
// function itself is synchronous (`suggestColumnsInScope` is a pure walk over
// the cached snapshot) — TanStack Query is used for the React-friendly stale
// state + suspense ergonomics, not for any deferred fetching.
//
// Suggestions are recomputed lazily on each render via `enabled`; when the
// resolver service has no cached snapshot the hook returns `data: []` and the
// popover renders the "No connection — edit as SQL" body.

import { useQuery } from '@tanstack/react-query';

import type { QualifiedName } from '../../../sql-grammar/model';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';
import type { Column, JoinCandidate, SchemaSnapshot, Table } from '../../../sql-resolver/types/schema';
import type { Scope } from '../../../sql-resolver/types/scope';

/**
 * The cache-key segment that says WHICH snapshot content a suggestion list was
 * computed from. A pulled snapshot's `revision` moves when the MetaInfo folded
 * into it changes (a loose FK → join candidate, a JSON declaration → column
 * classification) even though the native cache's `fetchedAt` does not; a
 * hand-built snapshot has no revision and keys on `fetchedAt` as before.
 */
export function snapshotCacheIdentity(snapshot: SchemaSnapshot | undefined): string | number | null {
  return snapshot?.revision ?? snapshot?.fetchedAt ?? null;
}

export type UseColumnSuggestionsArgs = {
  readonly service: SqlResolverService | null | undefined;
  /** The scope returned by the Resolver service for the current cursor. */
  readonly scope: Scope | null | undefined;
  readonly filter?: string;
  /** Optional cache key segment to invalidate alongside scope changes. */
  readonly cacheKey?: string;
};

export type UseColumnSuggestionsResult = {
  readonly columns: readonly Column[];
  readonly isLoading: boolean;
  readonly hasSnapshot: boolean;
};

export function useColumnSuggestions(
  args: UseColumnSuggestionsArgs,
): UseColumnSuggestionsResult {
  const { service, scope, filter, cacheKey } = args;
  const enabled = !!service && !!scope;
  const snapshot = service?.getSchemaSnapshot();
  const hasSnapshot = !!snapshot;

  const query = useQuery({
    queryKey: [
      'vqe-edit',
      'columns',
      service?.connectionId ?? null,
      service?.engineId ?? null,
      cacheKey ?? null,
      filter ?? null,
      // Including reachable-table identity in the key so a scope change (e.g.
      // user added a JOIN) invalidates the cache. We use folded names which
      // are stable across re-renders.
      scope?.reachableTables.map((rt) => rt.qualified.foldedName).join('|') ?? null,
      // Snapshot identity, like the table suggestions below: a list computed
      // before the snapshot landed must not stay cached once it does (a
      // one-table database leaves the reachable-table key unchanged).
      snapshotCacheIdentity(snapshot),
    ],
    queryFn: () => {
      if (!service || !scope) return [] as readonly Column[];
      return service.suggestColumnsInScope(scope, filter);
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    columns: query.data ?? [],
    isLoading: query.isLoading,
    hasSnapshot,
  };
}

export type UseTableSuggestionsArgs = {
  readonly service: SqlResolverService | null | undefined;
  /** The scope returned by the Resolver service for the current cursor. */
  readonly scope: Scope | null | undefined;
  readonly filter?: string;
  /** Optional cache key segment to invalidate alongside scope changes. */
  readonly cacheKey?: string;
};

export type UseTableSuggestionsResult = {
  readonly tables: readonly Table[];
  readonly isLoading: boolean;
  readonly hasSnapshot: boolean;
};

export function useTableSuggestions(
  args: UseTableSuggestionsArgs,
): UseTableSuggestionsResult {
  const { service, scope, filter, cacheKey } = args;
  const enabled = !!service && !!scope;
  const snapshot = service?.getSchemaSnapshot();
  const hasSnapshot = !!snapshot;

  const query = useQuery({
    queryKey: [
      'vqe-edit',
      'tables',
      service?.connectionId ?? null,
      service?.engineId ?? null,
      cacheKey ?? null,
      filter ?? null,
      // Snapshot identity invalidates the cache when a new snapshot lands.
      snapshotCacheIdentity(snapshot),
      // CTEs in scope must invalidate so newly-defined CTEs appear in the list.
      scope?.ctes.map((c) => c.name.foldedName).join('|') ?? null,
    ],
    queryFn: () => {
      if (!service || !scope) return [] as readonly Table[];
      return service.suggestTablesInScope(scope, filter);
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    tables: query.data ?? [],
    isLoading: query.isLoading,
    hasSnapshot,
  };
}

export type UseJoinSuggestionsArgs = {
  readonly service: SqlResolverService | null | undefined;
  readonly scope: Scope | null | undefined;
  /** The FROM-side table the user wants to join FROM. */
  readonly leftTable: QualifiedName | null | undefined;
  readonly cacheKey?: string;
};

export type UseJoinSuggestionsResult = {
  readonly candidates: readonly JoinCandidate[];
  readonly isLoading: boolean;
  readonly hasSnapshot: boolean;
};

export function useJoinSuggestions(
  args: UseJoinSuggestionsArgs,
): UseJoinSuggestionsResult {
  const { service, scope, leftTable, cacheKey } = args;
  const enabled = !!service && !!scope && !!leftTable;
  const snapshot = service?.getSchemaSnapshot();
  const hasSnapshot = !!snapshot;

  const query = useQuery({
    queryKey: [
      'vqe-edit',
      'join-candidates',
      service?.connectionId ?? null,
      service?.engineId ?? null,
      cacheKey ?? null,
      leftTable?.foldedSchema ?? null,
      leftTable?.foldedName ?? null,
      // Snapshot identity invalidates the cache when a new snapshot lands —
      // FK edges may have changed, and so may the MetaInfo loose FKs.
      snapshotCacheIdentity(snapshot),
    ],
    queryFn: () => {
      if (!service || !scope || !leftTable) return [] as readonly JoinCandidate[];
      return service.suggestJoinCandidates(scope, leftTable);
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    candidates: query.data ?? [],
    isLoading: query.isLoading,
    hasSnapshot,
  };
}
